import { workflowRunPath } from '@/constants/workflow-editor';
import { orgPath } from '@/utils/org-path';
import { formatBytes } from '@/utils/format-bytes';
import {
  NotificationItem,
  TriggerFileEntry,
  TriggerRunEntry,
} from '@/types/notification-types';

export interface TriggerBatchItem {
  key: string;
  label: string;
  detail: string | null;
  target: string | null;
}

const UNTITLED = 'Untitled file';

const asArray = <T>(value: unknown): T[] =>
  Array.isArray(value) ? (value as T[]) : [];

const fileItems = (
  files: TriggerFileEntry[],
  detail: (file: TriggerFileEntry) => string | null,
): TriggerBatchItem[] =>
  files.map((file) => ({
    key: file.event_id,
    label: file.name || UNTITLED,
    detail: detail(file),
    target: null,
  }));

const sizeDetail = (file: TriggerFileEntry): string | null =>
  file.size_bytes != null ? formatBytes(file.size_bytes) : null;

const reasonDetail = (file: TriggerFileEntry): string | null =>
  file.reason ?? null;

const runItems = (
  runs: TriggerRunEntry[],
  workflowId: string | undefined,
): TriggerBatchItem[] =>
  runs.map((run) => ({
    key: run.run_id,
    label: run.file_name || UNTITLED,
    detail: null,
    target: workflowId
      ? orgPath(workflowRunPath(workflowId, run.run_id))
      : null,
  }));

// A batch renders as a list only when there is more than one member; a single
// file or run is already named in the title.
export const getTriggerBatchItems = (
  notification: NotificationItem,
): TriggerBatchItem[] => {
  const metadata = notification.metadata ?? {};
  let items: TriggerBatchItem[] = [];
  if (notification.event_type === 'trigger_files_detected') {
    items = fileItems(asArray<TriggerFileEntry>(metadata.files), sizeDetail);
  } else if (notification.event_type === 'trigger_files_failed') {
    items = fileItems(asArray<TriggerFileEntry>(metadata.files), reasonDetail);
  } else if (notification.event_type === 'trigger_runs_started') {
    items = runItems(
      asArray<TriggerRunEntry>(metadata.runs),
      metadata.workflow_id as string | undefined,
    );
  }
  return items.length > 1 ? items : [];
};
